import { Moon, Keyboard, LayoutGrid, Sun } from 'lucide-react';
import type { router as AppRouter } from '../router';
import { useUi } from '../stores/ui';
import { modulesIn } from '../lib/modules';
import { canView } from '@nest-recruit/shared';
import { useSession } from '../stores/session';
import { NAV_ITEMS, canOpenModule, destinationFor } from './nav-config';
import { registerCommands, type Command } from './command-registry';

// The shell's own commands. Every later slice calls `registerCommands` from its
// own module rather than editing this file (06-qol §1) — that is the whole point
// of the registry.

export function registerShellCommands(router: typeof AppRouter): () => void {
  const navigate: Command[] = NAV_ITEMS.map((item) => ({
    id: `nav.${item.id}`,
    label: item.label,
    section: 'navigate',
    context: item.goKey === undefined ? 'Screen' : `Screen · g then ${item.goKey}`,
    // Permission-filtered before display: a destination the viewer cannot open
    // never appears, not even for the frame before it is filtered out. Uses the
    // module bar's own rule, so the palette and the bar can never disagree about
    // where a given role is allowed to go.
    canSee: (roles, modules) => canOpenModule(item, roles, modules),
    icon: item.icon,
    keywords: [item.id],
    run: () => void router.navigate({ to: destinationFor(item, useSession.getState().session, modulesIn(router.options.context.queryClient)) }),
  }));

  const actions: Command[] = [
    {
      id: 'demo.open',
      label: 'Open the component demo',
      section: 'action',
      context: 'S2 · design system',
      icon: LayoutGrid,
      // R1: a developer page, offered to the HR Lead only (admin configuration is theirs, §L23).
      canSee: (session) => canView(session, 'admin_config'),
      keywords: ['components', 'design', 'tokens', 'demo'],
      run: () => void router.navigate({ to: '/demo' }),
    },
    {
      id: 'ui.toggleTheme',
      label: 'Toggle light and dark mode',
      section: 'action',
      context: 'Appearance',
      icon: useUi.getState().theme === 'dark' ? Sun : Moon,
      keywords: ['theme', 'dark', 'light', 'appearance'],
      run: () => useUi.getState().toggleTheme(),
    },
    {
      id: 'ui.shortcuts',
      label: 'Show keyboard shortcuts',
      section: 'action',
      context: 'Help',
      shortcut: '?',
      icon: Keyboard,
      keywords: ['keys', 'help', 'shortcuts'],
      run: () => useUi.getState().toggleShortcutSheet(),
    },
  ];

  return registerCommands([...actions, ...navigate]);
}
